// Página de chat privado entre alumnos (agregado 15/10/2026).
// Ruta: /chat  ·  /chat?con=<uid> abre directamente la conversación.
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Badge,
  Button,
  Card,
  Empty,
  Grid,
  Input,
  message as antMessage,
  Popconfirm,
  Spin,
  Tooltip,
  Typography,
} from "antd";
import {
  ArrowLeftOutlined,
  FlagOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import DashboardLayout from "../components/DashboardLayout";
import { useAuth } from "../contexts/AuthContext";
import profileIcon from "../assets/profile.png";
import {
  ChatMessage,
  getChatId,
  getOtherMemberId,
  isChatUnread,
  MarkChatRead,
  MAX_MESSAGE_LENGTH,
  PublicProfile,
  ReportChatMessage,
  SendChatMessage,
} from "../firebase-chat";
import { useChatMessages, useMyChats, usePublicProfiles } from "../hooks/useChat";

const formatTime = (date: Date | null) => {
  if (!date) return "";
  const d = dayjs(date);
  if (d.isSame(dayjs(), "day")) return d.format("HH:mm");
  if (d.isSame(dayjs().subtract(1, "day"), "day")) return `Ayer ${d.format("HH:mm")}`;
  return d.format("DD/MM HH:mm");
};

const panelStyle: React.CSSProperties = {
  borderRadius: 24,
  border: "1px solid var(--dashboard-panel-border)",
  boxShadow: "0 16px 40px rgba(10, 19, 53, 0.18)",
  height: "calc(100vh - 140px)",
  minHeight: 480,
};

const ProfileAvatar: React.FC<{ profile?: PublicProfile; size?: number }> = ({
  profile,
  size = 40,
}) => (
  <Avatar
    size={size}
    src={profile?.PortraitImg || profileIcon}
    style={{ flexShrink: 0, background: "#fff4c4" }}
  />
);

const Chat: React.FC = () => {
  const { user } = useAuth();
  const myUid = user?.uid ?? "";
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const [searchParams, setSearchParams] = useSearchParams();
  const otherUid = searchParams.get("con") || "";
  const openConversation = (uid: string | null) => {
    setSearchParams(uid ? { con: uid } : {});
  };

  const { chats, isLoading: chatsLoading } = useMyChats(myUid);
  const { profiles, byId, isLoading: profilesLoading } = usePublicProfiles();

  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);

  const chatId = myUid && otherUid ? getChatId(myUid, otherUid) : null;
  const currentChat = chats.find((c) => c.id === chatId) ?? null;
  const otherProfile = byId.get(otherUid);
  const { messages, hasMore, isLoading: messagesLoading, loadOlder } =
    useChatMessages(chatId);

  // ── Lista de la izquierda: conversaciones, o resultados de búsqueda ──
  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return profiles.filter(
      (p) => p.id !== myUid && p.Nombre.toLowerCase().includes(q),
    );
  }, [profiles, search, myUid]);

  // ── Marcar como leído al ver mensajes nuevos de la otra persona ──
  useEffect(() => {
    if (!currentChat || !myUid) return;
    if (document.visibilityState !== "visible") return;
    if (isChatUnread(currentChat, myUid)) {
      MarkChatRead(currentChat.id, myUid).catch((error) =>
        console.error("No se pudo marcar como leído:", error),
      );
    }
  }, [currentChat, myUid]);

  // ── Auto-scroll: abajo al abrir y al llegar mensajes si ya estabas abajo ──
  const scrollRef = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);
  const loadingOlder = useRef(false);
  const prevScrollHeight = useRef(0);

  useEffect(() => {
    stickToBottom.current = true;
  }, [chatId]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (loadingOlder.current) {
      // Mantener la posición al cargar mensajes anteriores arriba.
      el.scrollTop = el.scrollHeight - prevScrollHeight.current;
      loadingOlder.current = false;
    } else if (stickToBottom.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const handleLoadOlder = () => {
    const el = scrollRef.current;
    prevScrollHeight.current = el?.scrollHeight ?? 0;
    loadingOlder.current = true;
    loadOlder();
  };

  // ── Enviar ──
  const handleSend = async () => {
    const text = draft.trim();
    if (!text || !otherUid || sending) return;
    setSending(true);
    stickToBottom.current = true;
    try {
      await SendChatMessage({
        myUid,
        otherUid,
        text,
        chatExists: Boolean(currentChat),
      });
      setDraft("");
    } catch (error) {
      console.error(error);
      antMessage.error("No se pudo enviar el mensaje. Probá de nuevo.");
    } finally {
      setSending(false);
    }
  };

  const handleReport = async (msg: ChatMessage) => {
    if (!chatId) return;
    try {
      await ReportChatMessage({ chatId, message: msg, myUid });
      antMessage.success("Mensaje reportado. El equipo del Desafío lo va a revisar.");
    } catch (error) {
      console.error(error);
      antMessage.error("No se pudo reportar el mensaje.");
    }
  };

  // ════════════════════════ Columna izquierda ════════════════════════
  const listPanel = (
    <Card
      className="app-glass"
      style={panelStyle}
      bodyStyle={{ padding: 16, height: "100%", display: "flex", flexDirection: "column", gap: 12 }}
    >
      <Typography.Title level={4} style={{ margin: 0, color: "var(--dashboard-header-blue)" }}>
        Mensajes
      </Typography.Title>
      <Input.Search
        placeholder="Buscar compañero por nombre"
        allowClear
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <div style={{ flex: 1, overflowY: "auto", margin: "0 -8px" }}>
        {search.trim() ? (
          profilesLoading ? (
            <Spin style={{ display: "block", marginTop: 24 }} />
          ) : searchResults.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No encontramos a nadie con ese nombre."
            />
          ) : (
            searchResults.map((p) => (
              <ListRow
                key={p.id}
                profile={p}
                subtitle={`Nivel ${p.Nivel}`}
                selected={p.id === otherUid}
                onClick={() => {
                  openConversation(p.id);
                  setSearch("");
                }}
              />
            ))
          )
        ) : chatsLoading ? (
          <Spin style={{ display: "block", marginTop: 24 }} />
        ) : chats.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Todavía no tenés conversaciones. Buscá a un compañero para escribirle."
          />
        ) : (
          chats.map((c) => {
            const otherId = getOtherMemberId(c, myUid);
            const profile = byId.get(otherId);
            const mine = c.UltimoAutorId === myUid;
            return (
              <ListRow
                key={c.id}
                profile={profile}
                subtitle={`${mine ? "Vos: " : ""}${c.UltimoMensaje}`}
                time={formatTime(c.ActualizadoEl)}
                unread={isChatUnread(c, myUid)}
                selected={otherId === otherUid}
                onClick={() => openConversation(otherId)}
              />
            );
          })
        )}
      </div>
    </Card>
  );

  // ════════════════════════ Columna derecha ════════════════════════
  const conversationPanel = (
    <Card
      className="app-glass"
      style={panelStyle}
      bodyStyle={{ padding: 0, height: "100%", display: "flex", flexDirection: "column" }}
    >
      {!otherUid ? (
        <div style={{ flex: 1, display: "grid", placeItems: "center", padding: 24 }}>
          <Empty description="Elegí una conversación o buscá a un compañero para empezar." />
        </div>
      ) : (
        <>
          {/* Encabezado */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "14px 18px",
              borderBottom: "1px solid var(--dashboard-panel-border)",
            }}
          >
            {isMobile && (
              <Button
                shape="circle"
                icon={<ArrowLeftOutlined />}
                onClick={() => openConversation(null)}
                aria-label="Volver a la lista"
              />
            )}
            <ProfileAvatar profile={otherProfile} size={44} />
            <div style={{ minWidth: 0 }}>
              <Typography.Text strong style={{ color: "var(--dashboard-header-blue)", fontSize: 16 }}>
                {otherProfile?.Nombre ?? "Alumno"}
              </Typography.Text>
              <div style={{ color: "var(--dashboard-text)", fontSize: 12 }}>
                {otherProfile ? `Nivel ${otherProfile.Nivel}` : ""}
              </div>
            </div>
          </div>

          {/* Mensajes */}
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            style={{ flex: 1, overflowY: "auto", padding: "16px 18px", display: "flex", flexDirection: "column", gap: 6 }}
          >
            {hasMore && (
              <Button size="small" onClick={handleLoadOlder} style={{ alignSelf: "center", marginBottom: 8 }}>
                Ver mensajes anteriores
              </Button>
            )}
            {messagesLoading && messages.length === 0 ? (
              <Spin style={{ margin: "auto" }} />
            ) : messages.length === 0 ? (
              <Typography.Text style={{ margin: "auto", color: "var(--dashboard-text)", textAlign: "center" }}>
                Mandale el primer mensaje a {otherProfile?.Nombre ?? "tu compañero"}.
              </Typography.Text>
            ) : (
              messages.map((m) => (
                <MessageBubble
                  key={m.id}
                  message={m}
                  mine={m.AutorId === myUid}
                  onReport={() => handleReport(m)}
                />
              ))
            )}
          </div>

          {/* Caja de texto */}
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "flex-end",
              padding: 12,
              borderTop: "1px solid var(--dashboard-panel-border)",
            }}
          >
            <Input.TextArea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  void handleSend();
                }
              }}
              placeholder="Escribí un mensaje (Shift + Enter para nueva línea)"
              autoSize={{ minRows: 1, maxRows: 5 }}
              maxLength={MAX_MESSAGE_LENGTH}
              style={{ borderRadius: 16 }}
            />
            <Button
              type="primary"
              shape="circle"
              size="large"
              icon={<SendOutlined />}
              onClick={() => void handleSend()}
              loading={sending}
              disabled={!draft.trim()}
              aria-label="Enviar mensaje"
            />
          </div>
        </>
      )}
    </Card>
  );

  return (
    <DashboardLayout>
      <div style={{ maxWidth: 1300, margin: "0 auto", width: "100%" }}>
        {isMobile ? (
          otherUid ? conversationPanel : listPanel
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "340px 1fr", gap: 16 }}>
            {listPanel}
            {conversationPanel}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

// ─────────────────────────── Subcomponentes ───────────────────────────

const ListRow: React.FC<{
  profile?: PublicProfile;
  subtitle: string;
  time?: string;
  unread?: boolean;
  selected?: boolean;
  onClick: () => void;
}> = ({ profile, subtitle, time, unread, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      display: "flex",
      alignItems: "center",
      gap: 12,
      width: "100%",
      padding: "10px 8px",
      border: 0,
      borderRadius: 14,
      cursor: "pointer",
      textAlign: "left",
      font: "inherit",
      background: selected ? "var(--dashboard-panel-soft)" : "transparent",
    }}
  >
    <Badge dot={unread} offset={[-4, 4]}>
      <ProfileAvatar profile={profile} />
    </Badge>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
        <span
          style={{
            fontWeight: unread ? 700 : 600,
            color: "var(--dashboard-header-blue)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {profile?.Nombre ?? "Alumno"}
        </span>
        {time && <span style={{ fontSize: 11, color: "var(--dashboard-text)", flexShrink: 0 }}>{time}</span>}
      </div>
      <div
        style={{
          fontSize: 13,
          color: "var(--dashboard-text)",
          fontWeight: unread ? 600 : 400,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {subtitle}
      </div>
    </div>
  </button>
);

const MessageBubble: React.FC<{
  message: ChatMessage;
  mine: boolean;
  onReport: () => void;
}> = ({ message, mine, onReport }) => (
  <div
    style={{
      display: "flex",
      justifyContent: mine ? "flex-end" : "flex-start",
      alignItems: "flex-end",
      gap: 4,
    }}
  >
    <div
      style={{
        maxWidth: "75%",
        padding: "8px 12px",
        borderRadius: 16,
        borderBottomRightRadius: mine ? 4 : 16,
        borderBottomLeftRadius: mine ? 16 : 4,
        background: mine ? "var(--dashboard-header-blue)" : "var(--dashboard-panel-soft)",
        color: mine ? "var(--dashboard-white)" : "var(--dashboard-text)",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      {message.Texto}
      <div style={{ fontSize: 10, opacity: 0.7, textAlign: "right", marginTop: 2 }}>
        {formatTime(message.Fecha)}
      </div>
    </div>
    {!mine && (
      <Popconfirm
        title="¿Reportar este mensaje?"
        description="Lo va a revisar el equipo del Desafío."
        okText="Reportar"
        cancelText="Cancelar"
        onConfirm={onReport}
      >
        <Tooltip title="Reportar">
          <Button
            type="text"
            size="small"
            icon={<FlagOutlined />}
            aria-label="Reportar mensaje"
            style={{ opacity: 0.45 }}
          />
        </Tooltip>
      </Popconfirm>
    )}
  </div>
);

export default Chat;
